<template>
  <main class="soundcore">
    <Breadcrumb :items="breadcrumb" />

    <section class="soundcore__section soundcore__stage">
      <div class="soundcore__stage-background">
        <CdnImage
          src="/content/content-images/landingpages/soundcore/b3db16e03d596385ae35c5e33bd0bd34/header-visual@2x.jpg"
          alt="Ein man der Musik über sein Handy hört"
          sizes="xs:100vw sm:100vw md:100vw lg:vw"
        />
      </div>

      <GridContainer class="soundcore__stage-container">
        <Heading
          font-weight="ultra"
          size="lg"
          tag="h1"
          class="soundcore__stage-header"
        >
          <CdnImage
            v-if="logo?.logo"
            :src="logo.logo"
            :alt="logo.name"
            :image-style="`aspect-ratio: ${logo.aspectRatio}`"
            class="soundcore__stage-header-logo"
            render-without-figure
          />
          Fantastischer Sound, geliebt von über 52 Millionen Fans weltweit.
        </Heading>

        <div class="soundcore__stage-categories">
          <LinkButton
            tag="nuxt-link"
            to="/zubehoer/kopfhoerer/_soundcore"
            no-styling
            class="soundcore__stage-category-item"
          >
            <CdnImage
              src="/content/content-images/landingpages/soundcore/02e2778f36fda227c105efebb30a0b55/soundcore-kopfhoerer.webp"
              alt="Over-Ear und In-Ear Kopfhörer"
              render-without-figure
              style="aspect-ratio: 294 / 317"
            />
            Kopfhörer
          </LinkButton>
          <LinkButton
            tag="nuxt-link"
            to="/zubehoer/lautsprecher/_soundcore"
            no-styling
            class="soundcore__stage-category-item"
          >
            <CdnImage
              src="/content/content-images/landingpages/soundcore/1ea4c4d3ec7d51e86e089aa0b2f66144/nav-boxen@2x.png"
              alt="soundcore Bluetooth Lautsprecher"
              style="aspect-ratio: 347 / 310"
              render-without-figure
            />
            Lautsprecher
          </LinkButton>
        </div>
      </GridContainer>
    </section>

    <section class="soundcore__section soundcore__offer-tile-xl-container">
      <Heading
        tag="h2"
        size="lg"
        spaces-bottom
        align="center"
      >
        Der portable Lautsprecher von soundcore
      </Heading>
      <ProductOfferTileXlContainer
        v-for="(teaser, index) in getOffersByGroup(promotionsWithOffers, 'highlight')"
        :key="teaser.type"
        v-bind="teaser"
        class="soundcore__offer-tile-xl"
        :class="teaser.type"
        :alignment="index % 2 === 0 ? 'left' : 'right'"
        lazy
      >
        <template #heading>
          <Heading
            tag="h2"
            class="product-offer-tile-xl__title"
          >
            {{ teaser.name }}
          </Heading>
          <Paragraph
            v-if="teaser.description"
            class="product-offer-tile-xl__description"
            :text="teaser.description"
          />
        </template>
      </ProductOfferTileXlContainer>
    </section>

    <section class="soundcore__section soundcore__bassup">
      <GridContainer class="soundcore__bassup-container">
        <CdnImage
          src="/content/content-images/landingpages/soundcore/2720a8f7160f776afbc7afa845dc4a6c/bass-up-bg@2x.jpg"
          alt=""
          lazy
          sizes="xs:100vw sm:100vw md:100vw lg:1464px"
          class="soundcore__bassup-bg"
        />
        <Heading
          tag="h2"
          size="lg"
          class="soundcore__bassup-heading"
        >
          BassUp&#8482; 2.0:<br />
          Tiefer.Stärker.Smarter.
        </Heading>
        <p class="soundcore__bassup-content">
          16x stärkerer Bass für 40Hz ultra-tiefe Beats und präzise abgestimmte Tiefenfrequenzen,
          die ein beeindruckendes Klangerlebnis liefern.
        </p>
      </GridContainer>
    </section>

    <section class="soundcore__section soundcore__products">
      <ProductOfferTileSMContainer
        :items="getOffersByGroup(promotionsWithOffers, 'second')"
        tile-type="medium"
        heading-tag="h3"
      />
    </section>

    <section class="soundcore__section soundcore__slider">
      <ThemeworldSlider :entries="getOffersByGroup(promotionsWithOffers, 'slider')" />
    </section>

    <section class="soundcore__section soundcore__benefits">
      <GridContainer class="soundcore__benefits-container">
        <CdnImage
          v-if="logo?.logo"
          :src="logo.logo"
          :alt="logo.name"
          :image-style="`aspect-ratio: ${logo.aspectRatio}`"
          class="soundcore__benefits-logo"
          render-without-figure
        />
        <div class="soundcore__benefits-list">
          <div class="soundcore__benefits-list-entry">
            <CdnImage
              src="/content/content-images/landingpages/soundcore/a7ece57e65b1f9ce848733639e614e6a/icon-sound@2x.png"
              alt="Icon Sound"
              do-not-convert
              lazy
              image-style="aspect-ratio: 104 / 116"
              class="soundcore__benefits-icon"
              render-without-figure
            />

            Fantastischer Sound,<br />
            geliebt von über 52<br />
            Millionen Fans<br />
            weltweit.
          </div>
          <div class="soundcore__benefits-list-entry">
            <CdnImage
              src="/content/content-images/landingpages/soundcore/b01b4a63fe1f6f34578ce94a85c6dcf0/icon-tech@2x.png"
              alt="Icon Technik"
              do-not-convert
              lazy
              image-style="aspect-ratio: 104 / 116"
              class="soundcore__benefits-icon"
              render-without-figure
            />

            Weltweit führende<br />
            Audiotechnologien:<br />
            SweatGuard, BassUp,<br />
            ACAA und HearId.
          </div>
        </div>
      </GridContainer>
    </section>

    <ImageTeaser
      animate-tile
      position-y-desktop="center"
      lazy
      heading-tag="h2"
      style="--container-column-image: 0"
      class="soundcore__section soundcore__teaser"
      description="Erfahre hier, wie wir mitholfe der ACAA-Technolohie das Klangprofil erweitern, Verzerrungen eliminieren und die perfekte Balance zwischen tiefen Bässen und hohen Tönen erreichen."
      :background="{
        src: '/content/content-images/landingpages/soundcore/9225165df44d1fbca003e12bc7430732/technologie-teaser@2x.jpg',
        sizes: 'xs:100vw sm:100vw lg:100vw',
      }"
    >
      <template #heading>
        <div>
          <Heading
            tag="span"
            size="sm"
            >Ein Blick hinter die Kulissen</Heading
          >
          <Heading
            tag="h2"
            size="md"
            >souncdore Technologien</Heading
          >
        </div>
      </template>
      <template #button>
        <LinkButton
          :primary="true"
          tag="a"
          tealium-name="soundcore-acaa"
          target="_blank"
          is-external
          to="https://www.soundcore.com/de/acaa"
        >
          Mehr zu ACAA
        </LinkButton>
      </template>
    </ImageTeaser>

    <ImageTeaser
      animate-tile
      position-y-desktop="center"
      lazy
      heading-tag="h2"
      style="--container-column-image: 0"
      class="soundcore__section soundcore__teaser"
      :background="{
        src: '/content/content-images/landingpages/soundcore/e6dc0bce47b2b302d616f3a495ab16fa/weitere-produkte-teaser@2x.jpg',
        sizes: 'xs:100vw sm:100vw lg:100vw',
      }"
    >
      <template #heading>
        <div>
          <Heading
            tag="h2"
            size="md"
            >soundcore Produktvielfalt</Heading
          >
        </div>
      </template>
      <template #button>
        <LinkButton
          :primary="true"
          tag="nuxt-link"
          tealium-name="zu-soundcore"
          to="/zubehoer/_soundcore"
        >
          Alle Produkte
        </LinkButton>
      </template>
    </ImageTeaser>
  </main>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useLazyAsyncData } from '#imports';
import {
  GridContainer,
  Heading,
  ImageTeaser,
  LinkButton,
  Paragraph,
} from '@i22-td-smarthome/component-library';

import {
  getOffersByGroup,
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '@/lib/offers-by-group';

import Breadcrumb from '@/components/breadcrumb.vue';
import CdnImage from '@/components/cdn-image.vue';
import ProductOfferTileSMContainer from '@/components/product-offer-tile-s-m-container.vue';

import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import { addLogo } from '~/lib/brand';
import ProductOfferTileXlContainer from '~/components/product-offer-tile-xl-container.vue';
import ThemeworldSlider, {
  type ThemeworldSlidePromotion,
} from '~/components/anker/themeworld-slider.vue';

type SoundcorePromotion = {
  name?: string;
  description?: string;
} & Record<string, any>;

const TEASER_DATA: Promotion<SoundcorePromotion>[] = [
  {
    articleNumber: '99936069',
    tileGroups: ['highlight'],
    name: 'Select 4 Go Bluetooth Speaker Schwarz',
    description:
      '<ul class="list"><li class="list__element list__element--dashed">20 Stunden ultralange Akkulaufzeit</li><li class="list__element list__element--dashed"">Überraschend kraftvoller Sound</li> <li class="list__element list__element--dashed"">IP67 Wasser- sowie Staubschutz und schwimmfähig</li><li class="list__element list__element--dashed"">Leicht und einfach zutransportieren</li></ul>',
    ambientImage: {
      src: '/content/content-images/landingpages/soundcore/b37a2a00a2d1f037e7dacd558038b657/hero-teaser-select-4-go-bg@2x.jpg',
      alt: 'Select 4 Go mit vier Lautsprechern',
    },
    ambientImageSizes: 'xs:100vw md:100vw lg:80vw',
    productImage: {
      posY: {
        xs: '10%',
      },
    },
  },
  {
    articleNumber: '99935137',
    tileGroups: ['second'],
  },
  {
    articleNumber: '99935138',
    tileGroups: ['second'],
  },
  // {
  //   articleNumber: '99935822',
  //   tileGroups: ['second'],
  // },
];

const SLIDER_DATA: Promotion<ThemeworldSlidePromotion>[] = [
  {
    articleNumber: '99935137',
    tileGroups: ['slider'],
    heading: 'Unfold to Go mit fortschrittlichem Noise Cancelling',
    copy:
      'Erlebe den ultimativen Hörgenuss mit unseren kompakten, faltbaren Kopfhörern, die dank der FlexiCurve™-Struktur 50% kleiner sind. Das innovative 4-stufige Noise Cancelling System sorgt für Ruhe und Konzentration, während die speziellen Audiotreiber verzerrungsfreien Sound bieten.' +
      '<br/><br/>Genieße eine lange Wiedergabezeit von bis zu 60 Stunden und profitiere von ultraschnellem Laden - bereits 5 Minuten geben dir 8 Stunden Musikgenuss. Der bequeme Kopfbügel und die weichen Ohrmuscheln machen die Space One Pro perfekt für den ganzen Tag.',
    background: {
      src: '/content/content-images/landingpages/soundcore/51bc9c8a8cc6585f9e153c3a9074e3d0/slider-bg@2x.jpg',
      sizes: 'xs:100vw sm:100vw md:50vw lg:850px',
    },
  },
  {
    articleNumber: '99935138',
    tileGroups: ['slider'],
    heading: 'True-Wireless In-Ear Kopfhörer mit Geräuschunterdrückung',
    copy:
      'Genieße eindrucksvollen Klang mit den Liberty 4 NC-\n' +
      'Kopfhörern. Sie bieten eine erstklassige Geräuschunterdrückung, die dich von der Außenwelt abschirmt, und passen sich automatisch an deine Bedürfnisse an.<br />' +
      'Erlebe kraftvollen Sound mit hochwertigen Audiokomponenten, wahrend du Ihre Musik dank langer Akkulaufzeit stundenlang hören kannst. Mit praktischen Funktionen wie schnellem Koppeln und wasserdichtem Design sind diese Kopfhörer perfekt für unterwegs. Zudem sorgen spezielle Mikrofone für kristallklare Gespräche.',
    background: {
      src: '/content/content-images/landingpages/soundcore/51bc9c8a8cc6585f9e153c3a9074e3d0/slider-bg@2x.jpg',
      sizes: 'xs:100vw sm:100vw md:80vw lg:1050px',
    },
  },
];

Track({ page_type: 'theme.landing' });
getHeader({
  title: ref('soundcore Produkte entdecken'),
  isIndexed: ref(true),
  description: ref(
    'Fantastischer Sound mit weltweit führenden Audiotechnologien. Jetzt die Kopfhörer und Lautsprecher von soundcore entdecken!'
  ),
});

const { data: promotionsWithOffers, error: errorData } = useLazyAsyncData(
  'soundcore',
  (): Promise<PromotionOffer<SoundcorePromotion>[]> =>
    loadOffersForPromotions([...TEASER_DATA, ...SLIDER_DATA], false, true, false)
);

// handle errors
if (errorData.value) {
  console.error(errorData.value);
  // Show error page
  // throw useNuxtApp().nuxt2Context.error(errorData.value);
}

const breadcrumb = [
  {
    label: 'soundcore',
    to: 'soundcore',
  },
];

const logo = addLogo({ slug: 'soundcore', name: 'soundcore' });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.soundcore {
  &__section {
    margin-bottom: $grid-base * 6;

    @include mq($mq-sm) {
      margin-bottom: $grid-base * 8;
    }
  }

  &__stage {
    position: relative;

    @include mq($mq-sm) {
      margin-bottom: $grid-base * 12;
    }

    &-background {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
      background: #002;

      :deep(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &-container {
      padding-inline: $grid-base;
      height: 450px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;

      @include mq(false, $mq-sm - 1) {
        text-align: center;
      }

      @include mq($mq-sm) {
        height: 700px;
      }
    }

    &-header {
      text-align: center;
      max-width: 730px;
      margin-inline: auto;
      filter: invert(100%) brightness(2);
      margin-bottom: $grid-base * 2;

      @include mq(false, $mq-sm - 1) {
        padding-top: $grid-base * 2;
      }

      &-logo {
        display: block;
        width: 290px;
        margin-bottom: $grid-base;
        margin-inline: auto;

        @include mq($mq-sm) {
          margin-bottom: $grid-base * 2;
        }
      }
    }

    &-categories {
      display: flex;
      justify-content: center;
      border-top-right-radius: 25px;
      border-top-left-radius: 25px;
      margin-inline: $grid-base;
      padding: $grid-base * 2;
      background: linear-gradient(
        to bottom,
        rgb(from var(--color-bg) r g b / 0.5),
        var(--color-bg) 60%
      );

      grid-gap: $grid-base * 2;

      @include mq($mq-sm) {
        grid-gap: $grid-base * 4;
      }
    }

    &-category-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      font-weight: 700;

      > img {
        height: 80px;
        margin-bottom: $grid-base;

        @include mq($mq-sm) {
          height: 120px;
        }
      }

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__bassup {
    &-container {
      position: relative;
      height: 355px;

      display: flex;
      flex-direction: column;
      justify-content: center;
      color: var(--color-secondary-fixed);
    }

    &-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -1;
      background: #000;

      :deep(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center center;
      }
    }

    &-heading,
    &-content {
      margin-inline: $grid-base;

      @include mq($mq-sm) {
        margin-inline: $grid-base * 2;
      }
    }

    &-content {
      max-width: 500px;
    }
  }

  &__products {
    margin-top: $grid-base * 2;
    &-title {
      margin-bottom: $grid-base * 4;
    }
  }

  &__benefits {
    background-color: #f1f8fa;

    &-container {
      padding-block: $grid-base * 4;
      text-align: center;
    }

    &-logo {
      width: 300px;
      margin-bottom: $grid-base * 4;
    }

    &-list {
      display: flex;
      justify-content: center;

      @include mq(false, $mq-sm - 1) {
        flex-direction: column;
      }

      &-entry {
        font-weight: 800;
        font-size: 1.2em;
        line-height: 1.4em;
        padding: $grid-base * 4 $grid-base * 2;

        @include mq($mq-sm) {
          padding: 0 $grid-base * 4;
        }

        &:first-child {
          @include mq(false, $mq-sm - 1) {
            border-bottom: 1px solid currentColor;
          }
          @include mq($mq-sm) {
            border-right: 1px solid currentColor;
          }
        }
      }
    }

    &-icon {
      display: block;
      margin-inline: auto;
      margin-bottom: $grid-base * 2;
      width: 50px;
    }
  }

  &__teaser {
    margin-inline: auto;
    height: 520px;
    max-width: $grid-max;
    --container-column-content: 40%;

    @include mq(false, $mq-sm - 1) {
      text-align: center;
      justify-content: stretch;
    }

    :deep(.image-teaser__container) {
      @include mq(false, $mq-sm - 1) {
        margin-top: 36px;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
    }

    :deep(.image-teaser__background-image img) {
      aspect-ratio: 320 / 259;

      @include mq($mq-sm) {
        aspect-ratio: 1024 / 520;
      }

      @include mq($mq-md) {
        aspect-ratio: 1464 / 538;
      }
    }
  }
}
</style>
